import React, { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import { X, Upload, ChevronLeft, ChevronRight } from "lucide-react";
import exifr from "exifr";

const HarinaamBulkUpdate = ({ isOpen, onClose, onSave }) => {
  const [updateMode, setUpdateMode] = useState("daily");
  const [importMessage, setImportMessage] = useState("");
  const [monthlySankirtanCount, setMonthlySankirtanCount] = useState("1");
  const [monthlyParticipants, setMonthlyParticipants] = useState("");
  const [applyParticipantsToAll, setApplyParticipantsToAll] = useState(true);
  const [monthlyParticipantCounts, setMonthlyParticipantCounts] = useState([""]);
  const [applyDurationToAll, setApplyDurationToAll] = useState(true);
  const [monthlySharedDuration, setMonthlySharedDuration] = useState("");
  const [monthlyDurations, setMonthlyDurations] = useState([""]);
  const [monthlyImages, setMonthlyImages] = useState([]);
  const excelInputRef = useRef(null);
  const monthlyImageInputRef = useRef(null);
  // Selected month/year for calendar
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  // Calendar selection (day numbers like [14,15,...])
  const [selectedDates, setSelectedDates] = useState([]);

  // Rows to submit
  const [sankirtanData, setSankirtanData] = useState([]);

  // For showing quick validation error
  const [error, setError] = useState("");

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

  const formatDisplayDate = (day, month, year) => {
    const shortMonth = monthNames[month].slice(0, 3);
    return `${day} ${shortMonth} ${year}`;
  };

  // When modal opens, reset state
  useEffect(() => {
    if (isOpen) {
      setCurrentMonth(new Date().getMonth());
      setCurrentYear(new Date().getFullYear());
      setSelectedDates([]);
      setSankirtanData([]);
      setUpdateMode("daily");
      setError("");
      setImportMessage("");
      setMonthlySankirtanCount("1");
      setMonthlyParticipants("");
      setApplyParticipantsToAll(true);
      setMonthlyParticipantCounts([""]);
      setApplyDurationToAll(true);
      setMonthlySharedDuration("");
      setMonthlyDurations([""]);
      setMonthlyImages([]);
    }
  }, [isOpen]);

  const handleUpdateModeChange = (mode) => {
    if (mode === updateMode) return;
    setError("");
    setImportMessage("");
    setUpdateMode(mode);
    setSelectedDates([]);
    setSankirtanData([]);
    if (mode === "monthly") {
      setMonthlySankirtanCount("1");
      setMonthlyParticipants("");
      setApplyParticipantsToAll(true);
      setMonthlyParticipantCounts([""]);
      setApplyDurationToAll(true);
      setMonthlySharedDuration("");
      setMonthlyDurations([""]);
      setMonthlyImages([]);
    }
  };

  const downloadExcelSample = () => {
    if (updateMode !== "daily") return;
    const workbook = XLSX.utils.book_new();
    const today = new Date();
    const daily = XLSX.utils.aoa_to_sheet([
      ["Date (YYYY-MM-DD)", "Duration", "Participants"],
      [`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`, "30-60 mins", 1],
    ]);
    daily["!cols"] = [{ wch: 24 }, { wch: 20 }, { wch: 16 }];
    const instructions = XLSX.utils.aoa_to_sheet([
      ["Harinam Sankirtan daily score upload"],
      ["Use one row per Sankirtan date. Replace or delete the example row before uploading."],
      ["For a monthly update, use the Monthly Update form instead of Excel."],
      ["After importing, attach photos to the matching date rows in the Bulk Update window."],
      ["Duration must be one of: 0-30 mins, 30-60 mins, 60-90 mins, 90-120 mins, 120-150 mins, 150-180 mins."],
    ]);
    XLSX.utils.book_append_sheet(workbook, daily, "Daily");
    XLSX.utils.book_append_sheet(workbook, instructions, "Instructions");
    XLSX.writeFile(workbook, "harinaam-sankirtan-daily-score-sample.xlsx");
  };

  const handleExcelImport = async (event) => {
    if (updateMode !== "daily") {
      setError("Excel upload is available for Daily updates only.");
      return;
    }
    setError("");
    setImportMessage("");
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: true });
      const worksheet = workbook.Sheets.Daily || workbook.Sheets[workbook.SheetNames[0]];
      if (!worksheet) throw new Error("The workbook does not contain a score worksheet.");
      const records = XLSX.utils.sheet_to_json(worksheet, { defval: "", raw: true });
      if (!records.length) throw new Error("The Daily sheet has no score rows.");
      if (records.length > 500) throw new Error("Upload no more than 500 daily score rows at a time.");

      const normalizeHeader = (value) => String(value).trim().toLowerCase().replace(/[^a-z0-9]/g, "");
      const parseDate = (value) => {
        if (value instanceof Date && !Number.isNaN(value.getTime())) return { year: value.getFullYear(), month: value.getMonth(), day: value.getDate() };
        if (typeof value === "number") {
          const parsed = XLSX.SSF.parse_date_code(value);
          if (parsed) return { year: parsed.y, month: parsed.m - 1, day: parsed.d };
        }
        const input = String(value || "").trim();
        let match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(input);
        if (match) return { year: Number(match[1]), month: Number(match[2]) - 1, day: Number(match[3]) };
        match = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(input);
        if (match) return { year: Number(match[3]), month: Number(match[2]) - 1, day: Number(match[1]) };
        return null;
      };
      const allowedDurations = new Set(["0-30 mins", "30-60 mins", "60-90 mins", "90-120 mins", "120-150 mins", "150-180 mins"]);
      const rows = records.map((record, index) => {
        const values = Object.fromEntries(Object.entries(record).map(([key, value]) => [normalizeHeader(key), value]));
        const date = parseDate(values.date || values.dateyyyymmdd || values.sankirtandate);
        const duration = String(values.duration || "").trim();
        const participantValue = values.participants ?? values.people ?? values.peoples;
        if (!date) throw new Error(`Row ${index + 2}: enter a valid date (YYYY-MM-DD or DD-MM-YYYY).`);
        if (!allowedDurations.has(duration)) throw new Error(`Row ${index + 2}: choose a valid duration from the sample workbook.`);
        if (String(participantValue ?? "").trim() === "") throw new Error(`Row ${index + 2}: enter a Participants value.`);
        const participants = Number(participantValue);
        if (!Number.isInteger(participants) || participants < 0) throw new Error(`Row ${index + 2}: Participants must be a whole number of 0 or more.`);
        const checkDate = new Date(date.year, date.month, date.day);
        if (checkDate.getFullYear() !== date.year || checkDate.getMonth() !== date.month || checkDate.getDate() !== date.day) {
          throw new Error(`Row ${index + 2}: the date is not a valid calendar date.`);
        }
        return {
          id: `excel-${date.year}-${date.month}-${date.day}-${index}-${Date.now()}`,
          ...date,
          duration,
          peoples: String(participants[index]),
          images: [],
          imageLocations: [],
        };
      });
      setCurrentMonth(rows[0].month);
      setCurrentYear(rows[0].year);
      setSelectedDates(rows.map(({ day, month, year }) => ({ day, month, year })));
      setSankirtanData(rows);
      setError("");
      setImportMessage(`${rows.length} daily rows loaded. Add photos to each date row before submitting.`);
    } catch (importError) {
      setError(importError.message || "Could not read this Excel workbook. Use the sample file format.");
    }
  };

  const moveMonth = (offset) => {
    setError("");
    const next = new Date(currentYear, currentMonth + offset, 1);
    const nextMonth = next.getMonth();
    const nextYear = next.getFullYear();
    const now = new Date();
    if (updateMode === "monthly" && (nextYear > now.getFullYear() || (nextYear === now.getFullYear() && nextMonth > now.getMonth()))) return;
    setCurrentMonth(nextMonth);
    setCurrentYear(nextYear);
  };

  const handlePrevMonth = () => moveMonth(-1);
  const handleNextMonth = () => moveMonth(1);

  // Toggle calendar date selection, and sync rows
  const toggleDateSelection = (day) => {
    setError("");

    setSelectedDates((prev) => {
      const exists = prev.some(
        (d) =>
          d.day === day && d.month === currentMonth && d.year === currentYear
      );

      if (exists) {
        // Remove rows for this exact date
        setSankirtanData((rows) =>
          rows.filter(
            (r) =>
              !(
                r.day === day &&
                r.month === currentMonth &&
                r.year === currentYear
              )
          )
        );

        // Remove this exact date from selectedDates
        return prev.filter(
          (d) =>
            !(
              d.day === day &&
              d.month === currentMonth &&
              d.year === currentYear
            )
        );
      }

      // If date not already selected → add row if missing
      const hasRow = sankirtanData.some(
        (r) =>
          r.day === day && r.month === currentMonth && r.year === currentYear
      );

      if (!hasRow) {
        const newRow = {
          id: `${currentYear}-${currentMonth}-${day}-${Date.now()}`,
          day,
          month: currentMonth,
          year: currentYear,
          duration: "",
          peoples: "",
          images: [],
          imageLocations: [], // ⭐ added
        };
        setSankirtanData((rows) => [...rows, newRow]);
      }

      // Add this exact date to selectedDates
      return [...prev, { day, month: currentMonth, year: currentYear }];
    });
  };

  const handleDurationChange = (id, value) => {
    setError("");
    setSankirtanData((rows) =>
      rows.map((r) => (r.id === id ? { ...r, duration: value } : r))
    );
  };

  const handlePeopleChange = (id, value) => {
    setError("");
    const numeric = value.replace(/\D/g, "");
    setSankirtanData((rows) =>
      rows.map((r) => (r.id === id ? { ...r, peoples: numeric } : r))
    );
  };

  const handleImageUpload = async (id, event) => {
    setError("");
    const files = Array.from(event.target.files || []);
    event.target.value = "";
  
    const extractedLocations = [];
  
    for (const file of files) {
      try {
        const meta = await exifr.parse(file, true);

        console.log("EXIF metadata for", file.name, ":", meta);

        let latitude = null;
        let longitude = null;
        let source = "none";
  
        if (meta?.latitude && meta?.longitude) {
          latitude = meta.latitude;
          longitude = meta.longitude;
          source = "exif";
        }
  
        // ⭐ Extract BOTH DateTimeOriginal and CreateDate
        const dto = meta?.DateTimeOriginal instanceof Date ? meta.DateTimeOriginal : null;
        let cdate = null;

        if (meta?.CreateDate) {
          try {
            cdate = new Date(meta.CreateDate);   // Convert string → Date
          } catch (e) {
            cdate = null;
          }
        }
          
        // Format helpers
        const formatDate = (d) => d.toISOString().split("T")[0];
        const formatTime = (d) => d.toTimeString().split(" ")[0];
  
        // ⭐ Final date fields
        let taken_date = dto ? formatDate(dto) : null;
        let taken_time = dto ? formatTime(dto) : null;
  
        let created_date = cdate ? formatDate(cdate) : null;
        let created_time = cdate ? formatTime(cdate) : null;
  
        // If EXIF missing → fallback to NOW
        if (!taken_date || !taken_time) {
          const now = new Date();
          taken_date = formatDate(now);
          taken_time = formatTime(now);
        }
  
        if (!created_date || !created_time) {
          const now = new Date();
          created_date = formatDate(now);
          created_time = formatTime(now);
        }
  
        // Fallback GPS using device
        if (!latitude || !longitude) {
          await new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                latitude = pos.coords.latitude;
                longitude = pos.coords.longitude;
                source = "device";
                resolve();
              },
              () => resolve() // keep location null
            );
          });
        }
  
        extractedLocations.push({
          name: file.name,
          latitude,
          longitude,
          source,
  
          // ⭐ New fields
          taken_date,
          taken_time,
          created_date,
          created_time,
        });
  
      } catch (error) {
        console.log("EXIF error:", error);
        extractedLocations.push({
          name: file.name,
          latitude: null,
          longitude: null,
          source: "error",
          taken_date: null,
          taken_time: null,
          created_date: null,
          created_time: null,
        });
      }
    }
  
    // Update row with images + full metadata
    setSankirtanData((rows) =>
      rows.map((r) =>
        r.id === id
          ? { ...r, images: files, imageLocations: extractedLocations }
          : r
      )
    );
  };   

  // Add another row for same date
  const handleAddRowForDate = (row) => {
    setError("");
    const newRow = {
      id: `${row.year}-${row.month}-${row.day}-${Date.now()}-${Math.random()}`,
      day: row.day,
      month: row.month,
      year: row.year,
      duration: "",
      peoples: "",
      images: [],
      imageLocations: [],
    };
    setSankirtanData((rows) => [...rows, newRow]);
  };

  const today = new Date();
  const todayDay = today.getDate();
  const todayMonth = today.getMonth();
  const todayYear = today.getFullYear();

  const renderCalendar = () => {
    const days = [];
    const blanks = [];

    // Leading blanks
    for (let i = 0; i < firstDayOfMonth; i++) {
      blanks.push(
        <div
          key={`blank-${i}`}
          style={{ textAlign: "center", padding: "8px" }}
        />
      );
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const isSelected = selectedDates.some(
        (sel) =>
          sel.day === d &&
          sel.month === currentMonth &&
          sel.year === currentYear
      );

      // Determine if date is in the future (disable!)
      const isFuture =
        currentYear > todayYear ||
        (currentYear === todayYear && currentMonth > todayMonth) ||
        (currentYear === todayYear &&
          currentMonth === todayMonth &&
          d > todayDay);

      days.push(
        <div
          key={d}
          onClick={() => !isFuture && toggleDateSelection(d)}
          style={{
            textAlign: "center",
            padding: "8px",
            borderRadius: "4px",
            transition: "all 0.2s",

            backgroundColor: isSelected ? "#b91c1c" : "transparent",
            color: isFuture
              ? "#9ca3af" // greyed out
              : isSelected
                ? "white"
                : "#374151",

            cursor: isFuture ? "not-allowed" : "pointer",
            opacity: isFuture ? 0.4 : 1,
          }}
        >
          {d}
        </div>
      );
    }

    return [...blanks, ...days];
  };

  const handleMonthlyCountChange = (value) => {
    setMonthlySankirtanCount(value);
    const count = Number(value);
    const rowCount = Number.isInteger(count) && count > 0 ? Math.min(count, 500) : 0;
    setMonthlyDurations((durations) => Array.from({ length: rowCount }, (_, index) => durations[index] || ""));
    setMonthlyParticipantCounts((participants) => Array.from({ length: rowCount }, (_, index) => participants[index] || ""));
    setError("");
  };

  const handleMonthlyImageChange = (event) => {
    setMonthlyImages(Array.from(event.target.files || []));
    event.target.value = "";
    setError("");
  };

  const handleSubmit = () => {
    setError("");

    if (updateMode === "monthly") {
      const count = Number(monthlySankirtanCount);
      if (!Number.isInteger(count) || count < 1 || count > 500) {
        setError("Enter a number of Sankirtans between 1 and 500.");
        return;
      }
      const participants = applyParticipantsToAll
        ? Array.from({ length: count }, () => Number(monthlyParticipants))
        : monthlyParticipantCounts.slice(0, count).map(Number);
      const durations = applyDurationToAll
        ? Array.from({ length: count }, () => monthlySharedDuration)
        : monthlyDurations.slice(0, count);
      if (participants.some((value) => !Number.isInteger(value) || value < 1)) {
        setError("Enter a whole number of participants for every Sankirtan.");
        return;
      }
      if (durations.length !== count || durations.some((duration) => !duration)) {
        setError("Select the time taken for every Sankirtan.");
        return;
      }
      const monthlyRows = durations.map((duration, index) => ({
        id: `monthly-${currentYear}-${currentMonth}-${index}-${Date.now()}`,
        // Put each Sankirtan on a consecutive calendar day, beginning on day 1.
        // If there are more Sankirtans than days in the month, place extra events on the last day.
        day: Math.min(index + 1, daysInMonth),
        month: currentMonth,
        year: currentYear,
        duration,
        peoples: String(participants[index]),
        images: index === 0 ? monthlyImages : [],
        imageLocations: [],
      }));
      onSave && onSave(monthlyRows.map((row) => ({
        ...row,
        date: `${row.year}-${String(row.month + 1).padStart(2, "0")}-${String(row.day).padStart(2, "0")}`,
      })));
      return;
    }

    if (!sankirtanData.length) {
      setError("Please select at least one date and fill details.");
      return;
    }

    // Basic validation: duration + peoples required
    const invalidRow = sankirtanData.find(
      (r) => !r.duration || !r.peoples || Number(r.peoples) <= 0
    );
    if (invalidRow) {
      setError("Please fill Duration and Number of People for all rows.");
      return;
    }

    // Pass data to parent
    onSave &&
      onSave(
        sankirtanData.map((r) => ({
          ...r,
          date: `${r.year}-${String(r.month + 1).padStart(2, "0")}-${String(
            r.day
          ).padStart(2, "0")}`,
        }))
      );
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          padding: "16px",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            width: "100%",
            maxWidth: "1200px",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              borderBottom: "1px solid #e5e7eb",
              padding: "16px 24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "white",
            }}
          >
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: "#1f2937",
                margin: 0,
              }}
            >
              Bulk Update Your Harinaam Sankirtan Score
            </h2>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                color: "#9ca3af",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <X size={24} />
            </button>
          </div>

          <div style={{ padding: "24px", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", marginBottom: "20px" }}>
              <div style={{ display: "flex", gap: "8px" }} role="group" aria-label="Score update period">
                {[{ value: "daily", label: "Daily Update" }, { value: "monthly", label: "Monthly Update" }].map((option) => (
                  <button key={option.value} type="button" onClick={() => handleUpdateModeChange(option.value)} aria-pressed={updateMode === option.value}
                    style={{ padding: "9px 14px", borderRadius: "7px", border: updateMode === option.value ? "1px solid #9f1d21" : "1px solid #e5e7eb", background: updateMode === option.value ? "#9f1d21" : "#fff", color: updateMode === option.value ? "#fff" : "#374151", fontWeight: 600, cursor: "pointer" }}>
                    {option.label}
                  </button>
                ))}
              </div>
              {updateMode === "daily" && <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button type="button" onClick={downloadExcelSample} style={{ padding: "9px 14px", borderRadius: "7px", border: "1px solid #d7b66b", background: "#fffaf0", color: "#6b4a1f", fontWeight: 600, cursor: "pointer" }}>Sample Excel</button>
                <button type="button" onClick={() => excelInputRef.current?.click()} style={{ padding: "9px 14px", borderRadius: "7px", border: "1px solid #9f1d21", background: "#fff", color: "#9f1d21", fontWeight: 600, cursor: "pointer" }}><Upload size={16} style={{ marginRight: 6, verticalAlign: "text-bottom" }} />Upload Excel</button>
                <input ref={excelInputRef} type="file" accept=".xlsx,.xls" onChange={handleExcelImport} style={{ display: "none" }} />
              </div>}
            </div>
            <p style={{ margin: "-8px 0 16px", color: "#6b7280", fontSize: "13px" }}>
              Daily Excel rows keep photos with each date. Monthly mode records every Sankirtan from the first of the selected month and attaches the month’s photos once.
            </p>
            {importMessage && <div role="status" style={{ marginBottom: "12px", padding: "10px 12px", borderRadius: "6px", background: "#eff6ff", color: "#1e40af", fontSize: "13px" }}>{importMessage}</div>}
            <div className={`update-wrapper ${updateMode === "monthly" ? "monthly-update-wrapper" : ""}`}>
              {/* Calendar Section */}
              <div className="update-left">
                <h3
                  style={{
                    fontWeight: "500",
                    marginBottom: "16px",
                    color: "#374151",
                    fontSize: "16px",
                    alignItems: "center",
                  }}
                >
                  {updateMode === "daily" ? "Select Daily Score Dates" : "Select Monthly Score Period"}
                </h3>

                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "16px",
                  }}
                >
                  {/* Month Navigation */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "16px",
                    }}
                  >
                    <button
                      onClick={handlePrevMonth}
                      style={{
                        background: "none",
                        border: "none",
                        padding: "4px",
                        cursor: "pointer",
                        borderRadius: "4px",
                      }}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <div
                      style={{
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "15px",
                      }}
                    >
                      {monthNames[currentMonth]} {currentYear}
                    </div>
                    <button
                      onClick={handleNextMonth}
                      style={{
                        background: "none",
                        border: "none",
                        padding: "4px",
                        cursor: "pointer",
                        borderRadius: "4px",
                      }}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>

                  {updateMode === "daily" && <>
                  {/* Days of Week */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(7, 1fr)",
                      gap: "4px",
                      marginBottom: "8px",
                    }}
                  >
                    {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map(
                      (day) => (
                        <div
                          key={day}
                          style={{
                            fontSize: "11px",
                            textAlign: "center",
                            color: "#6b7280",
                            fontWeight: "500",
                          }}
                        >
                          {day}
                        </div>
                      )
                    )}
                  </div>

                  {/* Calendar Grid */}
                  <div className="update-calendar-container">
                    <div className="update-calendar-grid">
                      {renderCalendar()}
                    </div>
                  </div>
                  </>}
                  {updateMode === "monthly" && (
                    <div style={{ padding: "28px 16px", borderRadius: "8px", background: "#fff8eb", color: "#6b4a1f", textAlign: "center" }}>
                      Sankirtan entries are dated consecutively from 1 {monthNames[currentMonth]} {currentYear}. Enter one duration for each Sankirtan in the form.
                    </div>
                  )}
                </div>
              </div>

              {/* Data Entry Section */}
              <div className="update-right">
                <div className="update-content-inner">
                  {updateMode === "daily" ? <>
                  {/* Header Row */}
                  <div className="update-header">
                    <div>{updateMode === "monthly" ? "Month of Sankirtan" : "Date of Sankirtan"}</div>
                    <div>Duration</div>
                    <div>No. of People</div>
                    <div>{updateMode === "monthly" ? "All Monthly Images" : "Images for This Date"}</div>
                    <div>{updateMode === "daily" ? "Add" : ""}</div>
                  </div>

                  {/* Rows */}
                  {sankirtanData
                    .slice()
                    .sort((a, b) => {
                      return (
                        a.year - b.year || a.month - b.month || a.day - b.day
                      );
                    })
                    .map((item) => (
                      <div key={item.id} className="update-row">
                        {/* Date */}
                        <div>
                          <div className="mobile-label">Date of Sankirtan</div>
                          <div className="update-date">
                            {updateMode === "monthly" ? `${monthNames[item.month]} ${item.year}` : formatDisplayDate(item.day, item.month, item.year)}
                          </div>
                        </div>

                        {/* Duration */}
                        <div className="mobile-width">
                          <div className="mobile-label">Duration</div>
                          <select
                            value={item.duration}
                            onChange={(e) =>
                              handleDurationChange(item.id, e.target.value)
                            }
                            className="update-select"
                          >
                            <option value="">Select Duration</option>
                            <option value="0-30 mins">0–30 mins</option>
                            <option value="30-60 mins">30–60 mins</option>
                            <option value="60-90 mins">60–90 mins</option>
                            <option value="90-120 mins">90–120 mins</option>
                            <option value="120-150 mins">120–150 mins</option>
                            <option value="150-180 mins">150–180 mins</option>
                          </select>
                        </div>

                        {/* Peoples */}
                        <div className="mobile-width">
                          <div className="mobile-label">No. of People</div>
                          <input
                            type="number"
                            min={0}
                            value={item.peoples}
                            onChange={(e) =>
                              handlePeopleChange(item.id, e.target.value)
                            }
                            className="update-input"
                          />
                        </div>

                        {/* Upload */}
                        <div>
                          <div className="mobile-label">Upload Images</div>
                          <label className="update-upload-label">
                            <svg width="144" height="45" viewBox="0 0 144 45" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M132.017 0V0.5H133C133.348 0.5 133.691 0.516833 134.03 0.549805L134.077 0.0527344C134.806 0.123552 135.513 0.265708 136.193 0.47168L136.049 0.949219C136.713 1.15031 137.349 1.41536 137.95 1.7373L138.186 1.29785C138.823 1.6392 139.423 2.04063 139.978 2.49609L139.661 2.88281C140.193 3.3195 140.681 3.80738 141.117 4.33887L141.503 4.02148C141.958 4.5759 142.36 5.17618 142.701 5.81348L142.263 6.0498C142.585 6.65091 142.85 7.28745 143.051 7.95117L143.527 7.80566C143.733 8.48566 143.875 9.1932 143.946 9.92188L143.45 9.96973C143.483 10.3085 143.5 10.6523 143.5 11V11.958H144V13.875H143.5V15.792H144V17.708H143.5V19.625H144V21.542H143.5V23.458H144V25.375H143.5V27.292H144V29.208H143.5V31.125H144V33.042H143.5V34C143.5 34.3477 143.483 34.6915 143.45 35.0303L143.946 35.0771C143.875 35.8058 143.733 36.5133 143.527 37.1934L143.051 37.0488C142.85 37.7125 142.585 38.3491 142.263 38.9502L142.701 39.1855C142.36 39.8229 141.958 40.4231 141.503 40.9775L141.117 40.6611C140.681 41.1926 140.193 41.6805 139.661 42.1172L139.978 42.5029C139.423 42.9585 138.823 43.3598 138.186 43.7012L137.95 43.2627C137.349 43.5846 136.713 43.8497 136.049 44.0508L136.193 44.5273C135.513 44.7334 134.806 44.8755 134.077 44.9463L134.03 44.4502C133.691 44.4832 133.348 44.5 133 44.5H132.017V45H130.049V44.5H128.081V45H126.113V44.5H124.146V45H122.178V44.5H120.21V45H118.242V44.5H116.274V45H114.307V44.5H112.339V45H110.371V44.5H108.403V45H106.436V44.5H104.468V45H102.5V44.5H100.532V45H98.5645V44.5H96.5967V45H94.6289V44.5H92.6611V45H90.6934V44.5H88.7256V45H86.7578V44.5H84.79V45H82.8223V44.5H80.8545V45H78.8867V44.5H76.9189V45H74.9512V44.5H72.9844V45H71.0166V44.5H69.0488V45H67.0811V44.5H65.1133V45H63.1455V44.5H61.1777V45H59.21V44.5H57.2422V45H55.2744V44.5H53.3066V45H51.3389V44.5H49.3711V45H47.4033V44.5H45.4355V45H43.4678V44.5H41.5V45H39.5322V44.5H37.5645V45H35.5967V44.5H33.6289V45H31.6611V44.5H29.6934V45H27.7256V44.5H25.7578V45H23.79V44.5H21.8223V45H19.8545V44.5H17.8867V45H15.9189V44.5H13.9512V45H11.9834V44.5H11C10.6523 44.5 10.3085 44.4832 9.96973 44.4502L9.92188 44.9463C9.1932 44.8754 8.48566 44.7334 7.80566 44.5273L7.95117 44.0508C7.28745 43.8497 6.65091 43.5846 6.0498 43.2627L5.81348 43.7012C5.17618 43.3597 4.5759 42.9585 4.02148 42.5029L4.33887 42.1172C3.80738 41.6805 3.3195 41.1926 2.88281 40.6611L2.49609 40.9775C2.04063 40.4231 1.6392 39.8229 1.29785 39.1855L1.7373 38.9502C1.41536 38.3491 1.15031 37.7125 0.949219 37.0488L0.47168 37.1934C0.265708 36.5134 0.123553 35.8058 0.0527344 35.0771L0.549805 35.0303C0.516833 34.6915 0.5 34.3477 0.5 34V33.042H0V31.125H0.5V29.208H0V27.292H0.5V25.375H0V23.458H0.5V21.542H0V19.625H0.5V17.708H0V15.792H0.5V13.875H0V11.958H0.5V11C0.5 10.6523 0.516833 10.3085 0.549805 9.96973L0.0527344 9.92188C0.123615 9.19326 0.265653 8.48561 0.47168 7.80566L0.949219 7.95117C1.15031 7.28745 1.41536 6.65091 1.7373 6.0498L1.29785 5.81348C1.63923 5.17621 2.04061 4.57588 2.49609 4.02148L2.88281 4.33887C3.3195 3.80738 3.80738 3.3195 4.33887 2.88281L4.02148 2.49609C4.57588 2.04061 5.17621 1.63923 5.81348 1.29785L6.0498 1.7373C6.65091 1.41536 7.28745 1.15031 7.95117 0.949219L7.80566 0.47168C8.48561 0.265653 9.19326 0.123615 9.92188 0.0527344L9.96973 0.549805C10.3085 0.516833 10.6523 0.5 11 0.5H11.9834V0H13.9512V0.5H15.9189V0H17.8867V0.5H19.8545V0H21.8223V0.5H23.79V0H25.7578V0.5H27.7256V0H29.6934V0.5H31.6611V0H33.6289V0.5H35.5967V0H37.5645V0.5H39.5322V0H41.5V0.5H43.4678V0H45.4355V0.5H47.4033V0H49.3711V0.5H51.3389V0H53.3066V0.5H55.2744V0H57.2422V0.5H59.21V0H61.1777V0.5H63.1455V0H65.1133V0.5H67.0811V0H69.0488V0.5H71.0156V0H72.9834V0.5H74.9512V0H76.9189V0.5H78.8867V0H80.8545V0.5H82.8223V0H84.79V0.5H86.7578V0H88.7256V0.5H90.6934V0H92.6611V0.5H94.6289V0H96.5967V0.5H98.5645V0H100.532V0.5H102.5V0H104.468V0.5H106.436V0H108.403V0.5H110.371V0H112.339V0.5H114.307V0H116.274V0.5H118.242V0H120.21V0.5H122.178V0H124.146V0.5H126.113V0H128.081V0.5H130.049V0H132.017Z" fill="white" stroke="#C3C6D9" stroke-dasharray="2 2" />
                              <path d="M47.6414 33.96H46.7614C45.6947 32.6 45.1614 31.0767 45.1614 29.39C45.1614 28.5767 45.2981 27.7833 45.5714 27.01C45.8447 26.2367 46.2181 25.5433 46.6914 24.93H47.5614C46.5147 26.3433 45.9914 27.8433 45.9914 29.43C45.9914 31.03 46.5414 32.54 47.6414 33.96ZM48.2371 32L48.7271 25.26H49.7571L50.6471 27.72L51.7271 30.86C52.1138 29.7667 52.4938 28.72 52.8671 27.72L53.7971 25.26H54.8271L55.4171 32H54.6071L54.3771 29.34L54.1371 26.35C53.7838 27.3367 53.4204 28.3333 53.0471 29.34L52.1071 31.85H51.3371L50.4471 29.34L49.4271 26.41C49.4271 26.67 49.3638 27.6467 49.2371 29.34L49.0471 32H48.2371ZM59.5713 29.66H58.6913C58.2647 29.66 57.9447 29.7433 57.7313 29.91C57.518 30.0767 57.4113 30.2867 57.4113 30.54C57.4113 30.7933 57.4947 31 57.6613 31.16C57.828 31.3133 58.0713 31.39 58.3913 31.39C58.7513 31.39 59.038 31.29 59.2513 31.09C59.4647 30.8833 59.5713 30.6233 59.5713 30.31V29.66ZM56.9513 27.99V27.2C57.3313 26.9667 57.8313 26.85 58.4513 26.85C59.7247 26.85 60.3613 27.4967 60.3613 28.79V32H59.5813V31.4C59.3213 31.8333 58.848 32.05 58.1613 32.05C57.688 32.05 57.3047 31.91 57.0113 31.63C56.7247 31.35 56.5813 30.9967 56.5813 30.57C56.5813 30.0833 56.7647 29.7067 57.1313 29.44C57.5047 29.1667 58.0113 29.03 58.6513 29.03H59.5713V28.77C59.5713 28.37 59.4813 28.07 59.3013 27.87C59.1213 27.6633 58.818 27.56 58.3913 27.56C57.8513 27.56 57.3713 27.7033 56.9513 27.99ZM63.8632 29.41L65.5632 32H64.6532L63.4232 30L62.1532 32H61.2832L62.9832 29.4L61.3832 26.92H62.2932L63.4432 28.82L64.6032 26.92H65.5032L63.8632 29.41ZM66.2186 31.91C66.1053 31.7967 66.0486 31.6567 66.0486 31.49C66.0486 31.3233 66.1053 31.1833 66.2186 31.07C66.3319 30.9567 66.4719 30.9 66.6386 30.9C66.8053 30.9 66.9453 30.9567 67.0586 31.07C67.1719 31.1833 67.2286 31.3233 67.2286 31.49C67.2286 31.6567 67.1719 31.7967 67.0586 31.91C66.9453 32.0233 66.8053 32.08 66.6386 32.08C66.4719 32.08 66.3319 32.0233 66.2186 31.91ZM74.3802 25.97H71.4902V28.36H74.0302V29.07H71.4902V32H70.6802V25.26H74.3802V25.97ZM75.5727 32V26.93H76.3627V32H75.5727ZM75.5927 25.71C75.4994 25.61 75.4527 25.4867 75.4527 25.34C75.4527 25.1933 75.4994 25.07 75.5927 24.97C75.6927 24.87 75.8161 24.82 75.9627 24.82C76.1094 24.82 76.2327 24.87 76.3327 24.97C76.4327 25.07 76.4827 25.1933 76.4827 25.34C76.4827 25.4867 76.4327 25.61 76.3327 25.71C76.2327 25.8033 76.1094 25.85 75.9627 25.85C75.8161 25.85 75.6927 25.8033 75.5927 25.71ZM77.9067 32V24.93H78.6967V32H77.9067ZM83.4307 29.14V28.94C83.4307 28.52 83.3174 28.1867 83.0907 27.94C82.864 27.6867 82.5507 27.56 82.1507 27.56C81.7707 27.56 81.4474 27.6967 81.1807 27.97C80.914 28.2433 80.7574 28.6333 80.7107 29.14H83.4307ZM83.9007 31V31.74C83.5074 31.9467 83.0274 32.05 82.4607 32.05C81.694 32.05 81.074 31.8167 80.6007 31.35C80.134 30.8767 79.9007 30.2533 79.9007 29.48C79.9007 28.6733 80.1107 28.0367 80.5307 27.57C80.9507 27.1033 81.4907 26.87 82.1507 26.87C82.784 26.87 83.2874 27.0733 83.6607 27.48C84.034 27.8867 84.2207 28.4633 84.2207 29.21C84.2207 29.4233 84.204 29.62 84.1707 29.8H80.7107C80.7707 30.3 80.9607 30.6833 81.2807 30.95C81.6007 31.2167 82.0207 31.35 82.5407 31.35C83.1074 31.35 83.5607 31.2333 83.9007 31ZM90.6267 27.1V27.85C90.3201 27.6633 89.9001 27.57 89.3667 27.57C89.1001 27.57 88.8867 27.6333 88.7267 27.76C88.5734 27.88 88.4967 28.0367 88.4967 28.23C88.4967 28.35 88.5201 28.4567 88.5667 28.55C88.6134 28.6367 88.7034 28.7233 88.8367 28.81C88.9767 28.8967 89.0767 28.9567 89.1367 28.99C89.2034 29.0167 89.3434 29.0767 89.5567 29.17C89.9834 29.3433 90.3167 29.54 90.5567 29.76C90.7967 29.98 90.9167 30.2767 90.9167 30.65C90.9167 31.0833 90.7634 31.4267 90.4567 31.68C90.1567 31.9333 89.7434 32.06 89.2167 32.06C88.6034 32.06 88.1067 31.9567 87.7267 31.75V30.98C88.1734 31.2467 88.6534 31.38 89.1667 31.38C89.4734 31.38 89.7067 31.32 89.8667 31.2C90.0267 31.08 90.1067 30.9167 90.1067 30.71C90.1067 30.47 90.0167 30.2833 89.8367 30.15C89.6634 30.0167 89.3701 29.8667 88.9567 29.7C88.5767 29.5533 88.2701 29.37 88.0367 29.15C87.8101 28.93 87.6967 28.6333 87.6967 28.26C87.6967 27.84 87.8534 27.5067 88.1667 27.26C88.4867 27.0067 88.8834 26.88 89.3567 26.88C89.8834 26.88 90.3067 26.9533 90.6267 27.1ZM92.0278 32V26.93H92.8178V32H92.0278ZM92.0478 25.71C91.9545 25.61 91.9078 25.4867 91.9078 25.34C91.9078 25.1933 91.9545 25.07 92.0478 24.97C92.1478 24.87 92.2711 24.82 92.4178 24.82C92.5645 24.82 92.6878 24.87 92.7878 24.97C92.8878 25.07 92.9378 25.1933 92.9378 25.34C92.9378 25.4867 92.8878 25.61 92.7878 25.71C92.6878 25.8033 92.5645 25.85 92.4178 25.85C92.2711 25.85 92.1478 25.8033 92.0478 25.71ZM97.5418 26.93V27.51L94.9418 31.29H97.6818V32H94.0318V31.38L96.6018 27.63H94.1318V26.93H97.5418ZM102.083 29.14V28.94C102.083 28.52 101.97 28.1867 101.743 27.94C101.516 27.6867 101.203 27.56 100.803 27.56C100.423 27.56 100.1 27.6967 99.833 27.97C99.5664 28.2433 99.4097 28.6333 99.363 29.14H102.083ZM102.553 31V31.74C102.16 31.9467 101.68 32.05 101.113 32.05C100.346 32.05 99.7264 31.8167 99.253 31.35C98.7864 30.8767 98.553 30.2533 98.553 29.48C98.553 28.6733 98.763 28.0367 99.183 27.57C99.603 27.1033 100.143 26.87 100.803 26.87C101.436 26.87 101.94 27.0733 102.313 27.48C102.686 27.8867 102.873 28.4633 102.873 29.21C102.873 29.4233 102.856 29.62 102.823 29.8H99.363C99.423 30.3 99.613 30.6833 99.933 30.95C100.253 31.2167 100.673 31.35 101.193 31.35C101.76 31.35 102.213 31.2333 102.553 31ZM103.938 28.2C103.938 28.04 103.991 27.9033 104.098 27.79C104.211 27.6767 104.351 27.62 104.518 27.62C104.684 27.62 104.824 27.6767 104.938 27.79C105.051 27.9033 105.108 28.04 105.108 28.2C105.108 28.3667 105.051 28.5067 104.938 28.62C104.824 28.7267 104.684 28.78 104.518 28.78C104.351 28.78 104.211 28.7267 104.098 28.62C103.991 28.5067 103.938 28.3667 103.938 28.2ZM104.098 31.85C103.991 31.7367 103.938 31.5967 103.938 31.43C103.938 31.2633 103.991 31.1233 104.098 31.01C104.211 30.8967 104.351 30.84 104.518 30.84C104.684 30.84 104.824 30.8967 104.938 31.01C105.051 31.1233 105.108 31.2633 105.108 31.43C105.108 31.5967 105.051 31.7367 104.938 31.85C104.824 31.9567 104.684 32.01 104.518 32.01C104.351 32.01 104.211 31.9567 104.098 31.85ZM108.323 27.25V26.4L109.873 25.46H110.613V32H109.833V26.32L108.323 27.25ZM114.516 32L115.006 25.26H116.036L116.926 27.72L118.006 30.86C118.393 29.7667 118.773 28.72 119.146 27.72L120.076 25.26H121.106L121.696 32H120.886L120.656 29.34L120.416 26.35C120.063 27.3367 119.7 28.3333 119.326 29.34L118.386 31.85H117.616L116.726 29.34L115.706 26.41C115.706 26.67 115.643 27.6467 115.516 29.34L115.326 32H114.516ZM123.971 31.31H125.501C125.927 31.31 126.254 31.2133 126.481 31.02C126.714 30.82 126.831 30.53 126.831 30.15C126.831 29.77 126.701 29.4733 126.441 29.26C126.187 29.0467 125.824 28.94 125.351 28.94H123.971V31.31ZM123.971 25.95V28.25H125.051C125.591 28.25 125.991 28.1367 126.251 27.91C126.511 27.6833 126.641 27.39 126.641 27.03C126.641 26.7167 126.517 26.46 126.271 26.26C126.031 26.0533 125.671 25.95 125.191 25.95H123.971ZM123.161 25.26H125.191C125.884 25.26 126.437 25.42 126.851 25.74C127.271 26.06 127.481 26.49 127.481 27.03C127.481 27.4167 127.371 27.7533 127.151 28.04C126.937 28.32 126.661 28.4967 126.321 28.57C126.694 28.63 127.011 28.8 127.271 29.08C127.537 29.3533 127.671 29.7267 127.671 30.2C127.671 30.74 127.491 31.1767 127.131 31.51C126.777 31.8367 126.274 32 125.621 32H123.161V25.26ZM128.964 24.93C129.437 25.5433 129.81 26.2367 130.084 27.01C130.357 27.7833 130.494 28.5767 130.494 29.39C130.494 31.07 129.957 32.5933 128.884 33.96H128.004C129.104 32.54 129.654 31.03 129.654 29.43C129.654 27.83 129.13 26.33 128.084 24.93H128.964Z" fill="#353535" />
                              <rect x="4" y="4" width="37" height="37" rx="8" fill="#F5F5F5" />
                              <path d="M20.1243 27.0521C19.7997 27.0521 19.5306 26.7829 19.5306 26.4583V23.1413L18.9606 23.7113C18.731 23.9408 18.351 23.9408 18.1214 23.7113C17.8918 23.4817 17.8918 23.1017 18.1214 22.8721L19.7047 21.2888C19.871 21.1225 20.1322 21.0671 20.3539 21.1621C20.5756 21.2492 20.7181 21.4708 20.7181 21.7083V26.4583C20.7181 26.7829 20.4489 27.0521 20.1243 27.0521Z" fill="#9F1D21" />
                              <path d="M21.7083 23.8854C21.5579 23.8854 21.4075 23.83 21.2887 23.7112L19.7054 22.1279C19.4758 21.8983 19.4758 21.5183 19.7054 21.2887C19.935 21.0592 20.315 21.0592 20.5446 21.2887L22.1279 22.8721C22.3575 23.1017 22.3575 23.4817 22.1279 23.7112C22.0091 23.83 21.8587 23.8854 21.7083 23.8854Z" fill="#9F1D21" />
                              <path d="M24.8757 31.0104H20.1257C15.8269 31.0104 13.9902 29.1737 13.9902 24.875V20.125C13.9902 15.8262 15.8269 13.9896 20.1257 13.9896H24.084C24.4086 13.9896 24.6777 14.2587 24.6777 14.5833C24.6777 14.9079 24.4086 15.1771 24.084 15.1771H20.1257C16.4761 15.1771 15.1777 16.4754 15.1777 20.125V24.875C15.1777 28.5246 16.4761 29.8229 20.1257 29.8229H24.8757C28.5252 29.8229 29.8236 28.5246 29.8236 24.875V20.9167C29.8236 20.5921 30.0927 20.3229 30.4173 20.3229C30.7419 20.3229 31.0111 20.5921 31.0111 20.9167V24.875C31.0111 29.1737 29.1744 31.0104 24.8757 31.0104Z" fill="#9F1D21" />
                              <path d="M30.4173 21.5104H27.2507C24.5432 21.5104 23.4902 20.4575 23.4902 17.75V14.5833C23.4902 14.3458 23.6327 14.1242 23.8544 14.0371C24.0761 13.9421 24.3294 13.9975 24.5036 14.1638L30.8369 20.4971C31.0032 20.6633 31.0586 20.9246 30.9636 21.1463C30.8686 21.3679 30.6548 21.5104 30.4173 21.5104ZM24.6777 16.0163V17.75C24.6777 19.7925 25.2082 20.3229 27.2507 20.3229H28.9844L24.6777 16.0163Z" fill="#9F1D21" />
                              <path d="M51.72 10.248V11.46C51.368 11.268 51.048 11.136 50.76 11.064C50.48 10.984 50.12 10.944 49.68 10.944C48.872 10.944 48.196 11.22 47.652 11.772C47.116 12.316 46.848 13.04 46.848 13.944C46.848 14.864 47.116 15.596 47.652 16.14C48.188 16.684 48.896 16.956 49.776 16.956C50.552 16.956 51.204 16.756 51.732 16.356V17.604C51.236 17.932 50.548 18.096 49.668 18.096C48.46 18.096 47.472 17.72 46.704 16.968C45.936 16.216 45.552 15.208 45.552 13.944C45.552 12.72 45.936 11.724 46.704 10.956C47.48 10.188 48.46 9.804 49.644 9.804C50.508 9.804 51.2 9.952 51.72 10.248ZM53.1416 18V9.516H54.3296V18H53.1416ZM56.083 18V11.916H57.271V18H56.083ZM56.143 10.584C56.007 10.44 55.939 10.264 55.939 10.056C55.939 9.848 56.007 9.672 56.143 9.528C56.287 9.384 56.463 9.312 56.671 9.312C56.879 9.312 57.055 9.384 57.199 9.528C57.343 9.672 57.415 9.848 57.415 10.056C57.415 10.256 57.343 10.428 57.199 10.572C57.055 10.716 56.879 10.788 56.671 10.788C56.463 10.788 56.287 10.72 56.143 10.584ZM59.8524 14.988C59.8684 15.604 60.0564 16.112 60.4164 16.512C60.7764 16.904 61.2804 17.1 61.9284 17.1C62.4084 17.1 62.8564 16.968 63.2724 16.704V17.76C62.8804 18 62.3804 18.12 61.7724 18.12C60.8204 18.12 60.0604 17.82 59.4924 17.22C58.9244 16.62 58.6404 15.872 58.6404 14.976C58.6404 14.104 58.9244 13.36 59.4924 12.744C60.0684 12.12 60.8444 11.808 61.8204 11.808C62.3484 11.808 62.8324 11.92 63.2724 12.144V13.224C62.8804 12.976 62.4364 12.852 61.9404 12.852C61.3084 12.852 60.8004 13.052 60.4164 13.452C60.0404 13.852 59.8524 14.364 59.8524 14.988ZM64.6249 18V9.6H65.8009V18H64.6249ZM67.2049 14.82L69.5929 18H68.1889L65.8489 14.82L68.1409 11.916H69.5209L67.2049 14.82ZM75.102 12.828V16.176C75.102 16.76 75.382 17.052 75.942 17.052C76.27 17.052 76.546 16.964 76.77 16.788V17.844C76.506 17.988 76.182 18.06 75.798 18.06C74.542 18.06 73.914 17.424 73.914 16.152V12.828H72.966V11.916H73.914V10.428H75.102V11.916H76.698V12.828H75.102ZM80.6103 18.108C79.7143 18.108 79.0103 17.816 78.4983 17.232C77.9863 16.648 77.7303 15.908 77.7303 15.012C77.7303 14.1 77.9943 13.344 78.5223 12.744C79.0503 12.136 79.7463 11.832 80.6103 11.832C81.5383 11.832 82.2543 12.128 82.7583 12.72C83.2623 13.304 83.5143 14.052 83.5143 14.964C83.5143 15.876 83.2543 16.628 82.7343 17.22C82.2223 17.812 81.5143 18.108 80.6103 18.108ZM80.5983 12.828C80.0783 12.828 79.6743 13.036 79.3863 13.452C79.0983 13.86 78.9543 14.368 78.9543 14.976C78.9543 15.576 79.1023 16.084 79.3983 16.5C79.6943 16.908 80.0983 17.112 80.6103 17.112C81.1543 17.112 81.5663 16.912 81.8463 16.512C82.1343 16.112 82.2783 15.604 82.2783 14.988C82.2783 14.356 82.1423 13.84 81.8703 13.44C81.5983 13.032 81.1743 12.828 80.5983 12.828ZM92.7008 14.928V9.912H93.9368V14.904C93.9368 15.952 93.6648 16.748 93.1208 17.292C92.5768 17.836 91.8248 18.108 90.8648 18.108C89.9208 18.108 89.1768 17.844 88.6328 17.316C88.0888 16.78 87.8168 16.008 87.8168 15V9.912H89.0528V14.988C89.0528 15.676 89.2168 16.18 89.5448 16.5C89.8808 16.812 90.3208 16.968 90.8648 16.968C92.0888 16.968 92.7008 16.288 92.7008 14.928ZM98.429 17.052C98.901 17.052 99.289 16.868 99.593 16.5C99.897 16.124 100.049 15.6 100.049 14.928C100.049 14.296 99.901 13.796 99.605 13.428C99.309 13.052 98.933 12.864 98.477 12.864C98.021 12.864 97.625 13.032 97.289 13.368C96.961 13.696 96.797 14.22 96.797 14.94C96.797 15.644 96.961 16.172 97.289 16.524C97.625 16.876 98.005 17.052 98.429 17.052ZM96.797 11.916V12.852C97.213 12.172 97.837 11.832 98.669 11.832C99.469 11.832 100.101 12.124 100.565 12.708C101.029 13.284 101.261 14.02 101.261 14.916C101.261 15.836 101.021 16.596 100.541 17.196C100.069 17.788 99.437 18.084 98.645 18.084C97.837 18.084 97.221 17.748 96.797 17.076V20.328H95.621V11.916H96.797ZM102.63 18V9.516H103.818V18H102.63ZM108.067 18.108C107.171 18.108 106.467 17.816 105.955 17.232C105.443 16.648 105.187 15.908 105.187 15.012C105.187 14.1 105.451 13.344 105.979 12.744C106.507 12.136 107.203 11.832 108.067 11.832C108.995 11.832 109.711 12.128 110.215 12.72C110.719 13.304 110.971 14.052 110.971 14.964C110.971 15.876 110.711 16.628 110.191 17.22C109.679 17.812 108.971 18.108 108.067 18.108ZM108.055 12.828C107.535 12.828 107.131 13.036 106.843 13.452C106.555 13.86 106.411 14.368 106.411 14.976C106.411 15.576 106.559 16.084 106.855 16.5C107.151 16.908 107.555 17.112 108.067 17.112C108.611 17.112 109.023 16.912 109.303 16.512C109.591 16.112 109.735 15.604 109.735 14.988C109.735 14.356 109.599 13.84 109.327 13.44C109.055 13.032 108.631 12.828 108.055 12.828ZM115.501 15.288H114.589C114.141 15.288 113.801 15.376 113.569 15.552C113.345 15.728 113.233 15.952 113.233 16.224C113.233 16.504 113.321 16.724 113.497 16.884C113.673 17.044 113.929 17.124 114.265 17.124C114.649 17.124 114.949 17.02 115.165 16.812C115.389 16.596 115.501 16.316 115.501 15.972V15.288ZM112.441 13.368V12.228C112.913 11.956 113.537 11.82 114.313 11.82C115.905 11.82 116.701 12.592 116.701 14.136V18H115.537V17.28C115.225 17.792 114.677 18.048 113.893 18.048C113.317 18.048 112.853 17.884 112.501 17.556C112.157 17.22 111.985 16.796 111.985 16.284C111.985 15.7 112.205 15.248 112.645 14.928C113.085 14.6 113.701 14.436 114.493 14.436H115.501V14.088C115.501 13.672 115.401 13.36 115.201 13.152C115.001 12.936 114.669 12.828 114.205 12.828C113.557 12.828 112.969 13.008 112.441 13.368ZM120.745 17.052C121.209 17.052 121.601 16.876 121.921 16.524C122.241 16.172 122.401 15.636 122.401 14.916C122.401 14.22 122.233 13.704 121.897 13.368C121.569 13.032 121.197 12.864 120.781 12.864C120.309 12.864 119.921 13.052 119.617 13.428C119.313 13.796 119.161 14.324 119.161 15.012C119.161 15.62 119.309 16.112 119.605 16.488C119.909 16.864 120.289 17.052 120.745 17.052ZM122.401 9.516H123.589V18H122.401V17.076C121.977 17.748 121.357 18.084 120.541 18.084C119.741 18.084 119.109 17.784 118.645 17.184C118.181 16.576 117.949 15.844 117.949 14.988C117.949 14.036 118.193 13.272 118.681 12.696C119.169 12.12 119.797 11.832 120.565 11.832C121.357 11.832 121.969 12.168 122.401 12.84V9.516Z" fill="#9F1D21" />
                            </svg>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={(e) => handleImageUpload(item.id, e)}
                              style={{ display: "none" }}
                            />
                          </label>
                          {item.images && item.images.length > 0 && (
                            <div className="update-file-list">
                              {item.images.map((f) => f.name).join(", ")}
                            </div>
                          )}
                        </div>

                        {/* Add / Remove Buttons */}
                        <div className="update-actions">
                          {/* + Button */}
                          {updateMode === "daily" && <button
                            type="button"
                            onClick={() => handleAddRowForDate(item)}
                            className="update-btn-circle"
                          >
                            +
                          </button>}

                          {/* - Button (only show if more than 1 row for same date) */}
                          {updateMode === "daily" && sankirtanData.filter(
                            (r) =>
                              r.day === item.day &&
                              r.month === item.month &&
                              r.year === item.year
                          ).length > 1 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setSankirtanData((rows) =>
                                    rows.filter((r) => r.id !== item.id)
                                  )
                                }
                                className="update-btn-circle"
                                style={{
                                  color: "#dc2626",
                                  fontWeight: "bold",
                                  fontSize: "20px",
                                }}
                              >
                                –
                              </button>
                            )}
                        </div>
                      </div>
                    ))}

                  {!sankirtanData.length && (
                    <p style={{ fontSize: "13px", color: "#6b7280", marginTop: "8px" }}>
                      Select dates from the calendar or import a Daily workbook to add rows here.
                    </p>
                  )}
                  </> : (
                    <div className="monthly-score-form" style={{ display: "grid", gap: "16px", padding: "8px 0" }}>
                      <div className="monthly-summary-grid">
                        <label style={{ display: "grid", gap: "6px", color: "#374151", fontWeight: 600, minWidth: 0 }}>
                          Number of Sankirtans this month
                          <input className="update-input" type="number" min="1" max="500" value={monthlySankirtanCount} onChange={(event) => handleMonthlyCountChange(event.target.value)} />
                        </label>
                        <div style={{ display: "grid", alignContent: "start", gap: "8px", minWidth: 0 }}>
                          <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", color: "#374151", fontWeight: 600, lineHeight: 1.4 }}>
                            <input type="checkbox" checked={applyParticipantsToAll} onChange={(event) => { setApplyParticipantsToAll(event.target.checked); setError(""); }} />
                            Same participants for all Sankirtans
                          </label>
                          {applyParticipantsToAll ? (
                            <input aria-label="Participants per Sankirtan" className="update-input" type="number" min="1" value={monthlyParticipants} onChange={(event) => { setMonthlyParticipants(event.target.value); setError(""); }} placeholder="Participants per Sankirtan" />
                          ) : (
                            <div className="monthly-entry-grid">
                              {monthlyParticipantCounts.map((participants, index) => (
                                <label key={index} className="monthly-entry-field">
                                  Sankirtan {index + 1} participants
                                  <input className="update-input" type="number" min="1" value={participants} onChange={(event) => setMonthlyParticipantCounts((values) => values.map((value, itemIndex) => itemIndex === index ? event.target.value : value))} />
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <h4 style={{ margin: "0 0 10px", fontSize: "15px", color: "#374151" }}>Time taken for each Sankirtan</h4>
                        <label style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px", color: "#374151", fontWeight: 600 }}>
                          <input type="checkbox" checked={applyDurationToAll} onChange={(event) => { setApplyDurationToAll(event.target.checked); setError(""); }} />
                          Same duration for all Sankirtans
                        </label>
                        {applyDurationToAll ? (
                          <select aria-label="Duration for all Sankirtans" className="update-select" value={monthlySharedDuration} onChange={(event) => { setMonthlySharedDuration(event.target.value); setError(""); }}>
                            <option value="">Select duration for all</option>
                            <option value="0-30 mins">0–30 mins</option>
                            <option value="30-60 mins">30–60 mins</option>
                            <option value="60-90 mins">60–90 mins</option>
                            <option value="90-120 mins">90–120 mins</option>
                            <option value="120-150 mins">120–150 mins</option>
                            <option value="150-180 mins">150–180 mins</option>
                          </select>
                        ) : (
                          <div className="monthly-entry-grid">
                            {monthlyDurations.map((duration, index) => (
                              <label key={index} className="monthly-entry-field">
                                Sankirtan {index + 1}
                                <select className="update-select" value={duration} onChange={(event) => setMonthlyDurations((values) => values.map((value, itemIndex) => itemIndex === index ? event.target.value : value))}>
                                  <option value="">Select duration</option>
                                  <option value="0-30 mins">0–30 mins</option>
                                  <option value="30-60 mins">30–60 mins</option>
                                  <option value="60-90 mins">60–90 mins</option>
                                  <option value="90-120 mins">90–120 mins</option>
                                  <option value="120-150 mins">120–150 mins</option>
                                  <option value="150-180 mins">150–180 mins</option>
                                </select>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{ padding: "14px", border: "1px solid #e5e7eb", borderRadius: "8px", background: "#fffdf9" }}>
                        <strong style={{ display: "block", marginBottom: "8px", color: "#374151" }}>Images for the whole month</strong>
                        <input ref={monthlyImageInputRef} type="file" accept="image/*" multiple onChange={handleMonthlyImageChange} />
                        <p style={{ margin: "8px 0 0", color: "#6b7280", fontSize: "13px" }}>
                          Monthly Sankirtans are dated from 1 {monthNames[currentMonth]} {currentYear}, one day per entry. Monthly photos are attached once to the first entry.
                        </p>
                        {monthlyImages.length > 0 && <small className="text-muted">{monthlyImages.length} image(s) selected</small>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Error text */}
            {error && (
              <div
                style={{
                  marginTop: "12px",
                  color: "#b91c1c",
                  fontSize: "13px",
                }}
              >
                {error}
              </div>
            )}

            {/* Footer with Update Button */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "24px",
                paddingTop: "16px",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <button
                style={{
                  backgroundColor: "#b91c1c",
                  color: "white",
                  padding: "10px 32px",
                  borderRadius: "6px",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: "500",
                  fontSize: "15px",
                }}
                onClick={handleSubmit}
              >
                Update Score
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HarinaamBulkUpdate;